import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { ServerService } from '../../../ts/services/ServerService';
import { SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { ServerUrls } from '../../ServerUrls';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { ServerSettingsSubspaceName } from '../../state/SpaceInfo';
import { ExtensionsSiteSettingsSubspace } from '../SiteSettingsSpace/ExtensionsSettingsSubspace';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { AddSiteSettingsSubspace } from './AddSiteSettingsSubspace';
import { CustomizationSettingsSubspace } from './CustomizationSettingsSubspace';
import { GeneralServerSettingsSubspace } from './GeneralServerSettingsSubspace';
import { LicensesSettingsSubspace } from './LicensesSettingsSubspace';

export interface ServerSettingsSpaceOwnProps extends ReactDirectiveProps {
  readonly subspaceName: ServerSettingsSubspaceName;
}

export interface ServerSettingsAngularProps {
  readonly server: ServerService;
  readonly browserTitleService: BrowserTitleService;
}

export interface ServerSettingsStateProps {
  readonly isSessionMultiSite: boolean;
}

export type ServerSettingsNonAngularProps = ServerSettingsSpaceOwnProps & ServerSettingsStateProps;
export type ServerSettingsSpaceProps = ServerSettingsNonAngularProps & ServerSettingsAngularProps;

export class ServerSettingsSpaceComponent extends React.PureComponent<ServerSettingsSpaceProps> {

  private static SubSpaceToTitle: {[P in ServerSettingsSubspaceName]: () => string} = {
    settings: Messages.serverSettings_general,
    licenses: Messages.serverSettings_licenses,
    extensions: Messages.serverSettings_extensions,
    addsite: Messages.serverSettings_addSite,
    customization: Messages.serverSettings_customization
  };

  public componentDidMount() {
    const title = ServerSettingsSpaceComponent.SubSpaceToTitle[this.props.subspaceName]();
    this.props.browserTitleService.setTitleWithTranslatedSpace({translatedSpaceName: title});
  }

  public render() {
    const subspaces = getSettingsSubspaces(
      this.props.angularContext,
      this.props.server,
      this.props.isSessionMultiSite,
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subspaceName}
        urlForSubspace={this.getSettingsUrl}
        mastheadElement={null}
        testIdPrefix={'server-settings'}
        spaceItemName={''}
      />
    );
  }

  private static settingsUrlBySubspace: Record<ServerSettingsSubspaceName, () => string> = {
    settings: () => ServerUrls.getInstance().settingsUrl(),
    extensions: () =>  ServerUrls.getInstance().settingsExtensionsUrl(),
    addsite: () =>  ServerUrls.getInstance().addSiteUrl(),
    licenses: () =>  ServerUrls.getInstance().settingsLicensesUrl(),
    customization: () => ServerUrls.getInstance().settingsCustomizationUrl()
  };

  private getSettingsUrl = (subspaceName?: ServerSettingsSubspaceName) => {
    if (subspaceName) {
      return ServerSettingsSpaceComponent.settingsUrlBySubspace[subspaceName]();
    }
    return '';
  }
}

function getSettingsSubspaces(
  angularContext: AngularContext,
  server: ServerService,
  isSessionMultiSite: boolean,
): ReadonlyArray<Subspace> {
  return _.compact([
    {
      name: 'settings',
      tabLabel: Messages.general(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <GeneralServerSettingsSubspace
          angularContext={angularContext}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
        />
      )
    },
    {
      name: 'licenses',
      tabLabel: Messages.licenses(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <LicensesSettingsSubspace
          angularContext={angularContext}
        />
      )
    },
    FeatureFlags.extensionsSettingsTabEnabled && {
      name: 'extensions',
      tabLabel: Messages.extensionsSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <ExtensionsSiteSettingsSubspace
          siteName={undefined}
          urls={undefined}
          angularContext={angularContext}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={true}
        />
      )
    },
    FeatureFlags.serverWelcomeChannelEnabled && {
      name: 'customization',
      tabLabel: Messages.customizationSetting_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <CustomizationSettingsSubspace
          angularContext={angularContext}
        />
      )
    },
    !isSessionMultiSite && {
      name: 'addsite',
      tabLabel: Messages.addASite(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <AddSiteSettingsSubspace
          angularContext={angularContext}
        />
      )
    },
  ]);
}

export const ConnectedServerSettingsSpace: React.ComponentClass<ServerSettingsSpaceOwnProps & ServerSettingsAngularProps>
  = connect((state: AppState) => ({
    isSessionMultiSite: SessionUserSelectors.isSessionMultiSite(state),
  }))(ServerSettingsSpaceComponent);

const ServerSettingsSpaceAngularHOC = angularProviderHOC<ServerSettingsSpaceOwnProps, ServerSettingsAngularProps, ServerSettingsAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    browserTitleService: ServiceLocator.get<BrowserTitleService>('BrowserTitleService'),
    server: ServiceLocator.get<ServerService>('ServerService'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const ServerSettingsSpace: React.ComponentType<ServerSettingsSpaceOwnProps>
  = ServerSettingsSpaceAngularHOC(ConnectedServerSettingsSpace);
